import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import {
  BuilderCompContextValue,
  BuilderComponentSettingValue,
  BuilderDroppableElement,
  BuilderPage,
  ElementSettings,
  IActionRepeaterValue,
  IActionType,
  VariableActionOption,
  VariableSelectFeatureFlag,
  VariableTextFeatureFlag,
} from '@makyoapp/makyo-common'

import { IDB, IDBTable } from '@/core/db'

import { SpacingInputUnit } from './enums'

export interface TreeItem extends BuilderDroppableElement {
  collapsed?: boolean
  formErrorType?: string
}

export type TreeItems = TreeItem[]

export interface FlattenedItem extends TreeItem {
  depth: number
  index: number
}

export enum BuilderComponentSettingValidation {
  NotEmpty = 'not-empty',
  Number = 'number',
  URL = 'url',
  AudioFile = 'audio-file',
  VideoFile = 'video-file',
  LottieFile = 'lottie-file',
}

export enum BuilderComponentSettingType {
  Text = 'text',
  Number = 'number',
  NumberWithUnit = 'number-with-unit',
  Switch = 'switch',
  Visibility = 'visibility',
  Select = 'select',
  IconSelect = 'icon-select',
  SegmentedSelect = 'segmented-select',
  Slider = 'slider',
  SliderSelect = 'slider-select',
  DatePicker = 'date-picker',
  TimePicker = 'time-picker',
  VariableSelect = 'variable-select',
  VariableText = 'variable-text',
  ColorScheme = 'color-scheme',
  ChartScheme = 'chart-scheme',
  GridColumns = 'grid-columns',
  ScreenSelect = 'page-select',
  TableSelect = 'table-select',
  TableSelectWithColumns = 'table-select-with-columns',
  ColumnSelect = 'column-select',
  Spacing = 'spacing',
  SpacingSingle = 'spacing-single',
  Radius = 'radius',
  Icon = 'icon',
  ColorVariant = 'color-variant',
  ComponentAlignment = 'component-alignment',
  HorizontalAlignment = 'h-align',
  VerticalAlignment = 'v-align',
  TextAlignment = 'text-align',
  BottomMidleAlignment = 'bm-align',
  TypographySelect = 'typography-select',
  TypographyGroup = 'typography-group-select',
  Divider = 'divider',
  ColumnFilter = 'column-filter',
  //ColumnSort = 'column-sort',
  ColumnList = 'column-list',
  TableColumnList = 'table-column-list',
  DataTableColumnList = 'data-table-column-list',
  UserColumnFilter = 'user-column-filter',
  MenuSelect = 'menu-select',
  SortBySelect = 'sort-by-select',
  Group = 'group',
  SectionBackgroundGroup = 'section-background-group',
  LayoutColumnSelect = 'layout-column-select',
  DataSetLimit = 'data-set-limit',
  TabsItems = 'tabs-items',
  GroupSorting = 'group-sorting',
  DownloadLink = 'download-link',
}

export interface BuilderActionSetting {
  id: string
  label: string
  isSimple: boolean
  showLabel?: boolean
  actionTypes?: IActionType[]
  excludeActionTypes?: IActionType[]
  defaultValue?: Omit<IActionRepeaterValue, 'id'> | Omit<IActionRepeaterValue, 'id'>[]
  dependOn?: BuilderDependOn
  /**
   * id of component setting that provide data source
   */
  dataSource?: string
}

export interface BuilderDependOn {
  id: string
  showWhen: (
    value: BuilderComponentSettingValue | BuilderComponentSettingValue[] | undefined,
    elId: string | undefined,
    ancestors: FlattenedItem[],
    context?: BuilderCompContextValue,
    formContext?: {
      dataSource?: string
      dbTable?: IDBTable
    }
  ) => boolean | 'disabled'
}

export interface BuilderBindOn {
  id: string
  bindWhen: (
    value: BuilderComponentSettingValue | BuilderComponentSettingValue[] | undefined,
    elId: string | undefined,
    ancestors: FlattenedItem[],
    context?: BuilderCompContextValue
  ) => boolean | 'disabled'
}

export interface BuilderComponentSetting {
  groupItems?: Record<string, BuilderComponentSetting[]>
  id: string
  label: string
  required?: boolean
  defaultValue?: BuilderComponentSettingValue | BuilderComponentSettingValue[]
  /**
   * default value specifically for overlay context
   */
  defaultValueOverlay?: BuilderComponentSettingValue | BuilderComponentSettingValue[]
  /**
   * overwrite value when the state is disabled
   */
  disabledValue?: BuilderComponentSettingValue | BuilderComponentSettingValue[]
  /**
   * auto default on detail page
   */
  autoDefaultInDetailPage?: boolean
  validation?: BuilderComponentSettingValidation
  type: BuilderComponentSettingType
  placeholder?: string

  /**
   * clamp the value in all devices
   */
  clampInAllDevices?: boolean

  /**
   * used by `BuilderComponentSettingType.VariableSelect`
   */
  variableSelectSettings?: {
    flags: VariableSelectFeatureFlag
    multiple?: boolean
    columnTypes?: ColumnTypes[]
    inputType?: 'text' | 'number' | 'datetime'
    placeholder?: string
    actionType?: VariableActionOption
    /**
     * for use alongside other field which select table source
     */
    tableSourceId?: string
    bindForm?: boolean
    /**
     * form behavior (pattern) settings, it will validate the value
     */
    formValidation?: string
    /**
     * new onValueChange for variableSelect
     */
    onValueChange?: (nextVal: any, prevSettings: Record<string, any>, type: ColumnTypes) => void
    /**
     * Custom order for displaying flags in UI.
     * If provided, flags will be displayed in this order.
     * Flags not in this array will be displayed after in default order.
     */
    flagOrder?: VariableSelectFeatureFlag[]
  }
  /**
   * used by `BuilderComponentSettingType.VariableText`
   */
  variableTextSettings?: {
    flags: VariableTextFeatureFlag
    columnTypes?: ColumnTypes[]
    /**
     * for use alongside other field which select table source
     */
    tableSourceId?: string

    shouldUpdateComponentTitle?: boolean
    maxChars?: number
  }
  /**
   * used by `BuilderComponentSettingType.Select`
   */
  selectSettings?: {
    multiple?: boolean
    multiline?: boolean
    type?: 'dropdown' | 'chip'
    options:
      | { value: string | number; icon?: string; label: string }[]
      | ((
          elId: string,
          ancestors: FlattenedItem[],
          context?: BuilderCompContextValue
        ) => { value: string | number; icon?: string; label: string }[])
  }
  /**
   * used by `BuilderComponentSettingType.SegmentedSelect`
   */
  segmentedSelectSettings?: {
    options:
      | { value: string | number; label: string }[]
      | ((
          elId: string,
          ancestors: FlattenedItem[],
          context?: BuilderCompContextValue
        ) => { value: string | number; label: string }[])
  }
  /**
   * used by `BuilderComponentSettingType.IconSelect`
   */
  iconSelectSettings?: {
    multiple?: boolean
    options:
      | { id: string; icon: string; title: string; color?: string }[]
      // todo
      | ((
          elId: string,
          ancestors: FlattenedItem[],
          context?: BuilderCompContextValue
        ) => { id: string; icon: string; title: string; color?: string }[])
  }
  /**
   * used by `BuilderComponentSettingType.Text`
   */
  textSettings?: {
    prefix?: string
    suffix?: string
    shouldUpdateComponentTitle?: boolean
    maxChars?: number
  }
  /**
   * used by `BuilderComponentSettingType.Number`
   */
  numberSettings?: {
    step?: number
    min?: number
    max?: number
  }
  numberWithUnitSettings?: {
    step?: number
    min?: number
    //max?: number
    options: { value: string; label: string; max?: number }[]
  }
  /**
   * used by `BuilderComponentSettingType.Slider`
   */
  sliderSettings?: {
    min?: number
    max?: number
    step?: number
    range?: boolean
    unit?: string
    showMark?: boolean
    showUnit?: boolean
  }
  sliderSelectSettings?: {
    options?: number[]
  }
  screenSelectSettings?: {
    multiple?: boolean
    repeater?: boolean
    isLoginComponent?: boolean
  }
  gridColumnSettings?: {
    numColumnId: string
  }
  colorVariantSettings?: {
    schemeId: string
    /**
     * show every possible variant
     */
    fullColor?: 'bg' | 'fg' | 'both'
    showExtendedColors?: boolean
  }
  columnFilterSettings?: {
    tableSourceId?: string
    columnSourceId?: string
  }
  tableSelectWithColumnSettings?: {
    columnTypes?: ColumnTypes[]
  }
  columnSelectSettings?: {
    tableSourceId: string
    columnTypes?: ColumnTypes[]
    multiple?: boolean
    noAutoSelect?: boolean
    includeLookupType?: boolean
    bindDetailScreen?: BuilderBindOn | boolean
    bindForm?: boolean
    dependentKeysOnChange?: any[]
    /**
     * @deprecated
     */
    onValueChange?: (
      nextVal: string | string[] | undefined,
      elId: string | undefined,
      elementSettings: ElementSettings,
      page: BuilderPage,
      flattenedElements: FlattenedItem[],
      ancestors: FlattenedItem[],
      context: BuilderCompContextValue
    ) => void

    /**
     * new onValueChange
     *
     * - we can add also send more dependencies such as dbClient, formContext, etc later on
     */
    onValueChangeV2?: (nextVal: any, prevSettings: Record<string, any>) => void
  }
  menuSelectSettings?: {
    autoDefault?: boolean
    multiple?: boolean
  }
  sortBySelectSettings?: {
    tableSourceId: string
    columnTypes?: ColumnTypes[]
  }
  dependOn?: BuilderDependOn
  typographyGroupSettings?: {
    items: {
      id: string
      label: string
    }[]
  }
  sectionGroupSettings?: {
    items: {
      id: string
      label: string
    }[]
  }
  tableColumnListSettings?: {
    columnTypes?: ColumnTypes[]
    showCurrentUserLogin?: boolean
    showModal?: boolean
  }
  tableSelectSettings?: {
    detailScreenDependId?: string
    disabledWhenDetailScreen?: boolean
    includeSnapshot?: boolean
  }
  spacingSettings?: {
    max: {
      [SpacingInputUnit.Pixel]: number
      [SpacingInputUnit.Rem]: number
      [SpacingInputUnit.Em]: number
      [SpacingInputUnit.Percent]: number
    }
  }
  help?: string
  showLabel?: boolean
  forceHorizontal?: boolean
  /**
   * @private
   * @param v
   * @param dbClient
   * @param settings
   */
  onValueChange?: (v: any, settings: Record<string, any>, dbClient: IDB) => Record<string, any>
  imageSelectSettings?: {
    flags: VariableSelectFeatureFlag
    multiple?: boolean
    columnTypes?: ColumnTypes[]
    inputType?: 'text' | 'number'
    placeholder?: string
    actionType?: VariableActionOption
    tableSourceId?: string
  }
  videoSelectSettings?: {
    flags: VariableSelectFeatureFlag
    multiple?: boolean
    columnTypes?: ColumnTypes[]
    inputType?: 'text' | 'number'
    placeholder?: string
    actionType?: VariableActionOption
    tableSourceId?: string
  }
  selectIcon?: boolean
  showDeviceVisibility?: boolean
  groupSortingSettings?:
    | Record<
        string,
        {
          dependOn?: BuilderDependOn
          items: BuilderComponentSetting[]
        }
      >
    | undefined
  downloadLinkSettings?: {
    text: string
    link: string
    openInNewTab?: boolean
  }
}
